import * as React from 'react';
import * as format from 'date-fns/format';
import * as qs from 'qs';

import { FormGroup, Input, Label } from 'reactstrap';

import { Link } from 'react-router-dom';
import { AppState } from 'common/models/AppState';
import { DailyAccountSummary } from 'common/api/models/DailyAccountSummary';
import { EntityActions } from 'common/entity/EntityActions';
import { Loading } from 'common/components/Loading';
import { Money } from 'common/components/Money';
import { Page } from 'common/components/layout/Page/Page';
import { ReportApi } from 'common/api/resources/ReportApi';
import { RouteComponentProps } from 'react-router-dom';
import { toApiError } from 'common/utils/api';

export interface AgingReportProps extends RouteComponentProps<{}> {
  appState: AppState;
  entityActions: EntityActions;
}
export interface AgingReportState {
  summaries: DailyAccountSummary[];
  isLoading: boolean;
  error?: string;
}
export class AgingReport extends React.Component<AgingReportProps, AgingReportState> {
  state: AgingReportState = { summaries: [], isLoading: false };

  getDate(props?: AgingReportProps): string {
    props = props ? props : this.props;
    const date = qs.parse(props.location.search.substring(1)).date;
    return date ? date : format(new Date(), 'YYYY-MM-DD');
  }

  setDate(date: string) {
    this.props.history.push(this.props.match.url + '?' + qs.stringify({ date: date }));
  }

  reload(props: AgingReportProps) {
    this.setState({ isLoading: true });
    const date = this.getDate(props);
    ReportApi.findAccountSummaryByDate(date).then(
      res => {
        // reload every 500ms if we get a 202
        if (res.data === undefined) {
          const reload = () => {
            this.reload(props);
          };
          window.setTimeout(reload, 500);
        } else {
          this.setState({ summaries: res.data, isLoading: false });
        }
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  }

  componentDidMount() {
    this.reload(this.props);
  }

  componentDidUpdate(prevProps: AgingReportProps) {
    if (prevProps.location !== this.props.location) {
      this.reload(this.props);
    }
  }

  handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value <= format(new Date(), 'YYYY-MM-DD')) {
      this.setDate(e.target.value);
    }
  };

  render() {
    const date = this.getDate();
    const cols = 8;
    let balance0Total = 0;
    let balance30Total = 0;
    let balance60Total = 0;
    let balance90Total = 0;
    let balance91Total = 0;
    for (let sum of this.state.summaries) {
      balance0Total += sum.agingSummary.balance0!;
      balance30Total += sum.agingSummary.balance30!;
      balance60Total += sum.agingSummary.balance60!;
      balance90Total += sum.agingSummary.balance90!;
      balance91Total += sum.agingSummary.balance91!;
    }
    return (
      <Page title="Aging Report">
        <div className="card card-responsive">
          <div className="p-3 p-sm-4">
            <div className="mb-3">
              {this.state.error && (
                <div className="alert alert-danger" style={{ marginTop: 20 }}>
                  {this.state.error}
                </div>
              )}
              <div style={{ marginTop: 20 }}>
                <FormGroup className="form-inline" style={{ marginTop: 10 }}>
                  <Label style={{ marginRight: 10 }}>Date</Label>
                  <Input type="date" value={date ? date : ''} onChange={this.handleChangeDate} />
                </FormGroup>
              </div>
              <div style={{ marginTop: 20 }}>
                <table className="table table-striped table-hover" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Number</th>
                      <th className="text-right">Current</th>
                      <th className="text-right">1 to 30</th>
                      <th className="text-right">31 to 60</th>
                      <th className="text-right">61 to 90</th>
                      <th className="text-right">91 and over</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.isLoading && (
                      <tr>
                        <td colSpan={cols}>
                          <Loading />
                        </td>
                      </tr>
                    )}
                    {!this.state.isLoading &&
                      this.state.summaries.length === 0 && (
                        <tr>
                          <td colSpan={cols} style={{ textAlign: 'center' }}>
                            No active balances on this date.
                          </td>
                        </tr>
                      )}
                    {!this.state.isLoading &&
                      this.state.summaries.map(s => (
                        <tr key={s.id}>
                          <td>
                            <Link to={'/' + String(s.account!.type).toLowerCase() + 's/' + s.account!.number}>
                              {s.account!.name}
                            </Link>
                          </td>
                          <td>{s.account!.number}</td>
                          <td className="number">
                            <Money value={s.agingSummary.balance0} hideSymbol={true} />
                          </td>
                          <td className="number">
                            <Money value={s.agingSummary.balance30} hideSymbol={true} />
                          </td>
                          <td className="number">
                            <Money value={s.agingSummary.balance60} hideSymbol={true} />
                          </td>
                          <td className="number">
                            <Money value={s.agingSummary.balance90} hideSymbol={true} />
                          </td>
                          <td className="number">
                            <Money value={s.agingSummary.balance91} hideSymbol={true} />
                          </td>
                          <td className="number">
                            <Money
                              value={
                                s.agingSummary.balance0! +
                                s.agingSummary.balance30! +
                                s.agingSummary.balance60! +
                                s.agingSummary.balance90! +
                                s.agingSummary.balance91!
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    {!this.state.isLoading && (
                      <tr className="total">
                        <td colSpan={2}>Total</td>
                        <td className="number">
                          <Money value={balance0Total} />
                        </td>
                        <td className="number">
                          <Money value={balance30Total} />
                        </td>
                        <td className="number">
                          <Money value={balance60Total} />
                        </td>
                        <td className="number">
                          <Money value={balance90Total} />
                        </td>
                        <td className="number">
                          <Money value={balance91Total} />
                        </td>
                        <td className="number">
                          <Money
                            value={balance0Total + balance30Total + balance60Total + balance90Total + balance91Total}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Page>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Reports/AgingReport/AgingReport.tsx